import { z } from 'zod';

const stringToNumberPreprocessor = (value) => {
  const processed = z.string().transform(Number).safeParse(value);
  return processed.success ? processed.data : value;
};

const urlString = z.string().refine((s) => !/\s/.test(s), { message: 'No spaces allowed' });

const AdGroupSchema = z.object({
  id: z.number().nullable(),
  insertionOrderId: z.number(),
  name: z.string().min(1, { message: "Name can't be blank" }),
  maxBid: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative()),
  // The backend allows [""] as the value for pixel_urls for some reason.
  // I believe it was a front-end hack to allow the field to show up in the UI.
  // Nevertheless, we'll just stick with it or else it will annoy users to have to delete it
  // from the many existing ad groups that have [""] as pixel_urls.
  pixelUrls: z.array(z.object({ value: urlString.or(z.literal('')) })),
  manyLocations: z.boolean(),
  campaignUrls: z
    .array(z.object({ value: urlString.refine((s) => !/^$/.test(s), { message: 'An ad group URL is required' }) }))
    .min(1, {
      message: 'Ad Group URLs must have at least one valid URL',
    }),
  conquest: z.boolean(),
  paused: z.boolean(),
  trackerUrls: z.array(urlString),
  trackingTypes: z.array(z.string()),
  targetingsEnabled: z.object({
    EXCHANGES: z.boolean(),
    BLACKLISTED_CATEGORIES: z.boolean(),
    WHITELISTED_CATEGORIES: z.boolean(),
    WHITELISTED_DEVICES: z.boolean(),
    BLACKLISTED_DEVICES: z.boolean(),
    BRANDSAFE_SCORES: z.boolean(),
    BLACKLISTED_CARRIERS: z.boolean(),
    WHITELISTED_CARRIERS: z.boolean(),
    WHITELISTED_APP_NAMES: z.boolean(),
    BLACKLISTED_APP_NAMES: z.boolean(),
    BLACKLISTED_APP_AD_SPACE_IDS: z.boolean(),
    WHITELISTED_GENDERS: z.boolean(),
    DEVICE_COUNTRIES: z.boolean(),
    WHITELISTED_STATE_IDS: z.boolean(),
    WHITELISTED_NIELSEN_DMAS: z.boolean(),
    WHITELISTED_APP_AD_SPACE_IDS: z.boolean(),
    VIDEO_SIZE_TARGETING: z.boolean(),
    HOURS: z.boolean(),
    WEATHER: z.boolean(),
    POLLEN_COUNT: z.boolean(),
    LOCATION_SCORES: z.boolean(),
    AGE_TARGETING: z.boolean(),
    app_blacklist_threshold: z.boolean(),
    app_whitelist_tier: z.boolean(),
    min_conversion_rate: z.boolean(),
    conversion_rate_exclusion: z.boolean(),
    TEMPERATURE: z.boolean(),
    UV_INDEX: z.boolean(),
    VIDEO_SKIPPABILITY: z.boolean(),
    APP_OR_SITE: z.boolean(),
    deal_ids: z.boolean(),
    restricted_targeting: z.boolean(),
    WHITELISTED_BUNDLE_IDS: z.boolean(),
    BLACKLISTED_BUNDLE_IDS: z.boolean(),
    INCLUDED_APP_BUNDLES: z.boolean(),
    EXCLUDED_APP_BUNDLES: z.boolean(),
    app_vcr_blacklist_threshold: z.boolean(),
    app_vcr_whitelist_tier: z.boolean(),
    VIDEO_AD_POSITION: z.boolean(),
    whitelisted_deal_ids: z.boolean(),
    needs_tn_user_id: z.boolean(),
    location_fetch_enabled: z.boolean(),
    dv_custom_segment_inclusion: z.boolean(),
    dv_custom_segment_exclusion: z.boolean(),
    dv_ivt_prebid_filter: z.boolean(),
  }),
  complete: z.boolean(),
  contractId: z.number(),
  targetingProfile: z.object({
    id: z.number().or(z.null()),
    campaign_id: z.number().or(z.null()),
    app_whitelist_tier: z.number(),
    app_blacklist_threshold: z.preprocess(stringToNumberPreprocessor, z.number()),
    exchanges: z.array(z.string()),
    blacklisted_categories: z.array(z.string()),
    whitelisted_categories: z.array(z.string()),
    blacklisted_devices: z.array(z.unknown()),
    whitelisted_devices: z.array(z.string()),
    device_countries: z.array(z.string()),
    blacklisted_app_names: z.array(z.unknown()),
    whitelisted_app_names: z.array(z.unknown()),
    whitelisted_app_ad_space_ids: z.array(z.string()),
    blacklisted_carriers: z.array(z.string()),
    whitelisted_carriers: z.array(z.unknown()),
    whitelisted_states: z.array(z.unknown()),
    whitelisted_dmas: z.array(z.unknown()),
    needs_tn_user_id: z.boolean(),
    min_age: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative()).or(z.null()),
    max_age: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative()).or(z.null()),
    whitelisted_genders: z.array(z.string()),
    location_fetch_enabled: z.boolean(),
    min_location_score: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative().max(100).nullable()),
    max_location_score: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative().max(100).nullable()),
    hours: z.array(z.string()),
    weather: z.array(z.unknown()),
    pollen_count: z.array(z.unknown()),
    brandsafe_scores: z.array(z.string()),
    min_conversion_rate: z.string(),
    video_size_targeting: z.array(z.unknown()),
    video_skippability: z.string().or(z.null()),
    blacklisted_app_ad_space_ids: z.array(z.string()),
    whitelisted_state_ids: z.array(z.unknown()),
    whitelisted_nielsen_dmas: z.array(z.unknown()),
    app_or_site: z.string(),
    deal_ids: z.array(z.unknown()),
    restricted_targeting: z.string().or(z.null()),
    blacklisted_bundle_ids: z.array(z.string()),
    whitelisted_bundle_ids: z.array(z.string()),
    included_app_bundles: z.array(z.string()),
    excluded_app_bundles: z.array(z.string()),
    excluded_dmas: z.array(z.unknown()),
    excluded_states: z.array(z.unknown()),
    conversion_rate_exclusion: z.string(),
    video_ad_position: z.array(z.unknown()),
    app_vcr_blacklist_threshold: z.preprocess(stringToNumberPreprocessor, z.number()),
    app_vcr_whitelist_tier: z.number(),
    whitelisted_deal_ids: z.array(z.unknown()),
    included_app_ad_space_id_lists: z.array(z.string()),
    excluded_app_ad_space_id_lists: z.array(z.string()),
    included_app_bundles_lists: z.array(z.string()),
    excluded_app_bundles_lists: z.array(z.string()),
    dv_custom_segment_inclusion: z.array(z.string()),
    dv_custom_segment_exclusion: z.array(z.string()),
    dv_ivt_prebid_filter: z.boolean(),
  }),
  annotatedProfileTargeting: z
    .array(
      z.object({
        // For the reason the segment ID for a geocookie is a string.
        segmentId: z
          .number({
            invalid_type_error: 'Please select an audience.',
          })
          .or(z.string()),
        name: z.string().or(z.undefined()),
        type: z.string(),
        targetingDay: z.number(),
        cpm: z.number().or(z.undefined()),
      })
    )
    .nullable(),
  annotatedBlacklistedSegments: z
    .array(
      z.object({
        segmentId: z.number({
          invalid_type_error: 'Please select an audience.',
        }),
        name: z.string().or(z.undefined()),
        type: z.string(),
      })
    )
    .nullable(),
  videoTrackingUrls: z.object({
    creativeView: z.array(z.unknown()),
    start: z.array(z.unknown()),
    firstQuartile: z.array(z.unknown()),
    midpoint: z.array(z.unknown()),
    thirdQuartile: z.array(z.unknown()),
    complete: z.array(z.unknown()),
  }),
  secure: z.boolean(),
  targetType: z.array(z.string()),
  creativeSizes: z.array(z.unknown()),
  geocookieOrTargeting: z.boolean(),
  deviceTypeTargeting: z.string(),
  momentsType: z.string(),
  spendPercent: z.number().nonnegative().max(100).or(z.string().nullable()),
  status: z.string().nullable(),
  mediaType: z.optional(z.string()),
  targetStrategy: z.optional(z.string().nullable()),
  crossDeviceMode: z.optional(z.string().nullable()),
  pmpData: z
    .object({
      creativeApi: z.number(),
      creativeType: z.string(),
      dealBidFloor: z.preprocess(stringToNumberPreprocessor, z.number().nonnegative()),
      dealId: z.string(),
      creativeProperties: z.array(z.string()),
    })
    .nullable(),
  isExcludedLocationsFormDirty: z.boolean(),
  lciMeasurement: z.boolean(),
  locationsCount: z.number(),
});

export type AdGroup = z.infer<typeof AdGroupSchema>;

export default AdGroupSchema;
